import Close20 from '@carbon/icons-react/es/close/20';
import { notification, Space } from 'antd';
import React, { FC, ReactNode } from 'react';
import { QueryClientProvider } from 'react-query';
import { Provider } from 'react-redux';
import styled from 'styled-components';
import { TertiaryButton } from 'components/atoms/tertiary-button';
import {
  ProductiveHeading07,
  Text02,
  Text01,
  BodyShort01,
} from 'components/styles';
import { store } from 'data';
import { queryClient } from 'queries';
import { CheckmarkIcon } from './checmark';
import { ErrorIcon } from './error';
import { InformationIcon } from './information';

const ToastTitle = styled.div.attrs({
  'data-cy': 'toast.title',
})`
  ${ProductiveHeading07};
  ${Text02};
`;

const ToastDesc = styled.div.attrs({
  'data-cy': 'toast.description',
})`
  ${BodyShort01};
  ${Text01};
`;

type Placement = 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';

// TODO: Change API
export const toastService = ({
  toastContainer,
  duration,
  title,
  message,
  placement = 'bottomLeft',
  key,
}: {
  toastContainer?: any;
  title?: React.ReactNode | string;
  message: React.ReactNode | string;
  placement?: Placement;
  key?: string;
  duration?: number;
}): {
  success(): void;
  error(): void;
  info(): void;
} => {
  const config = {
    message: <ToastTitle>{title}</ToastTitle>,
    description: <ToastDesc>{message}</ToastDesc>,
    placement,
    duration,
    getContainer: () => {
      return toastContainer?.current || document.body;
    },
    closeIcon: <Close20 />,
    key,
  };

  const style = {
    success: {
      backgroundColor: 'var(--green-10)',
      borderLeft: '3px solid var(--green-50)',
    },
    error: {
      backgroundColor: 'var(--red-10)',
      borderLeft: '3px solid var(--red-50)',
    },
    info: {
      backgroundColor: 'var(--blue-10)',
      borderLeft: '3px solid var(--blue-50)',
    },
  };

  return {
    success: () => {
      return notification.success({
        ...config,
        style: style.success,
        icon: <CheckmarkIcon />,
      });
    },
    error: () => {
      return notification.error({
        ...config,
        style: style.error,
        icon: <ErrorIcon />,
      });
    },
    info: () => {
      return notification.info({
        ...config,
        style: style.info,
        icon: <InformationIcon />,
      });
    },
  };
};

interface Props {
  message: ReactNode;
  onClick?: () => void;
  buttonText?: string;
}

/* Note:: Do not want to use brand color in button inside toast. It is decision taken by the designer.
@LinK:: https://northstar.nitro.run/#task_drawer=310.9582 */
const CTA = styled(TertiaryButton)`
  border: solid 1px var(--primary-color);
  color: var(--primary-color);
  box-shadow: var(--shadow-highlight)
    rgba(var(--blue-60-in-rgb), var(--shadow-highlight-color-opacity));

  &:focus,
  &:active,
  &:hover {
    color: var(--white-100);
    background-color: var(--primary-color) !important;
  }
`;

export const ToastDescription: FC<Props> = ({
  message = '',
  onClick,
  buttonText = '',
}) => {
  return (
    <Space direction="vertical">
      <div data-cy="toast.description.custom-message-element">{message}</div>
      {onClick && (
        <QueryClientProvider client={queryClient}>
          <Provider store={store}>
            <CTA data-cy="toast.action-btn" size="small" onClick={onClick}>
              {buttonText}
            </CTA>
          </Provider>
        </QueryClientProvider>
      )}
    </Space>
  );
};
